import { useSubscription } from '@apollo/client';
import { useEffect, useMemo, useRef } from 'react';
import { gql } from '@apollo/client';

import { useCurrent } from '@/hooks/useCurrent';

const CART_UPDATES_SUBSCRIPTION = gql`
  subscription CartUpdates($input: CartSubscriptionInput) {
    cartUpdates(input: $input) {
      userId
      eventType
      timestamp
      itemId
      delta {
        updatedFields
        removedFields
      }
      itemChange {
        changedItems {
          _id
          description
          discountedPrice
          discountAmount
          eventId
          eventTicketId
          otherItemId
          isOnSale
          itemId
          membershipOrderId
          name
          planOptions
          planType
          quantity
          saleLabel
          total
          type
          unitPrice
          subTotal
          checkoutAttendeesDetails
        }
        removedItemId
        orderSummary {
          discount
          discountCode
          eventTicketTotal
          membershipTotal
          subTotal
          total
          creditAvailable
          creditApplied
          unusedAmount
          unusedAmountApplied
          creditIssued
          effectiveTotal
          membershipDetails {
            membershipName
            unitPrice
          }
          ticketDetails {
            quantity
            ticketName
            unitPrice
          }
        }
        cartMetadata {
          _id
          currentlyAppliedPromocode
          createdAt
          updatedAt
        }
      }
    }
  }
`;

import { cartActions } from '@/state/cart/cartState';

// Global subscription instance tracker to prevent multiple subscriptions
let globalSubscriptionActive = false;
let globalSubscriptionUserId: string | null = null;

export const useCartSubscription = (onCartUpdate?: () => void | Promise<void>) => {
  const { user } = useCurrent();
  
  // Use stable userId to prevent unnecessary subscription recreations
  const userId = user?._id;
  
  // Always provide variables object (even if userId is undefined) to prevent subscription disconnection
  // The `skip` option will handle preventing the subscription when userId is missing
  const subscriptionVariables = useMemo(() => {
    if (!userId) return { input: { userId: null } };
    return {
      input: {
        userId,
      },
    };
  }, [userId]);

  // Use ref to track subscription instance and prevent multiple subscriptions
  const subscriptionRef = useRef<{ userId: string | undefined; timestamp: number } | null>(null);
  
  // Track last processed update timestamp to prevent duplicate processing
  const lastProcessedTimestampRef = useRef<number | null>(null);
  
  const { data, loading, error } = useSubscription(CART_UPDATES_SUBSCRIPTION, {
    variables: subscriptionVariables,
    skip: !userId,
    shouldResubscribe: true, 
    fetchPolicy: 'no-cache', // Don't cache subscription data
    onSubscriptionData: ({ subscriptionData, client }) => {
      const cartUpdate = subscriptionData?.data?.cartUpdates;
      
      if (!cartUpdate) {
        return;
      }
    },
  });
  
  // Track subscription lifecycle and ensure proper connection
  useEffect(() => {
    if (userId && !loading && !error) {
      const now = Date.now();
      const prevSubscription = subscriptionRef.current;
      
      // Only log if this is a new subscription or userId changed
      if (!prevSubscription || prevSubscription.userId !== userId) {
        subscriptionRef.current = { userId, timestamp: now };
        globalSubscriptionActive = true;
        globalSubscriptionUserId = userId;
       
      }
    }
  }, [userId, loading, error]);

  useEffect(() => {
    if (error) {
      console.error('Cart subscription error:', error);
      cartActions.setSubscriptionConnected(false);
      globalSubscriptionActive = false;
    } else if (!loading && userId && !error) {
      cartActions.setSubscriptionConnected(true);
      globalSubscriptionActive = true;
      globalSubscriptionUserId = userId;
    } else if (!loading && !userId) {
      // User logged out or not available - mark as disconnected
      cartActions.setSubscriptionConnected(false);
      globalSubscriptionActive = false;
      globalSubscriptionUserId = null;
    }
  }, [error, loading, userId]); // Removed subscriptionVariables and data - not needed here
  
  // Process updates from data prop - single source of truth for processing
  // This ensures we only process each update once, even if onSubscriptionData is called multiple times
  useEffect(() => {
    if (!data?.cartUpdates) {
      return;
    }

    const cartUpdate = data.cartUpdates;
    const updateTimestamp = cartUpdate.timestamp;
    
    // Skip if we've already processed this update (prevent duplicate processing)
    if (lastProcessedTimestampRef.current === updateTimestamp) {
      return;
    }
    
    lastProcessedTimestampRef.current = updateTimestamp;
    
    try {
      // Handle different update strategies based on event source
      // Mutations publish full itemChange, change streams publish minimal delta
      if (cartUpdate.itemChange) {
        // Special handling for CART_CLEARED - need to clear all items
        if (cartUpdate.eventType === 'CART_CLEARED') {
          cartActions.updateCartFromItemChange({
            ...cartUpdate.itemChange,
            isCartCleared: true, // Flag to indicate cart was cleared
          });
        } else {
          // Full item change from explicit mutation - use it directly
          cartActions.updateCartFromItemChange(cartUpdate.itemChange);
        }
      } else if (cartUpdate.delta && cartUpdate.itemId) {
        // Minimal delta from change stream - apply optimistic update and refetch if needed
        const needsFullRefetch = cartActions.applyDeltaUpdate(
          cartUpdate.itemId,
          cartUpdate.eventType,
          cartUpdate.delta
        );
        
        // Refetch full cart if pricing/order summary might have changed
        if (needsFullRefetch && onCartUpdate) {
          // Trigger cart refresh via callback
          onCartUpdate();
        }
      } else {
        // Cart-level event or unknown - trigger refresh via callback
        if (onCartUpdate) {
          onCartUpdate();
        } else {
          // Fallback: trigger loading state if no callback
          cartActions.setLoading(true);
        }
      }
    } catch (updateError) {
      console.error('❌ Error updating cart from subscription:', updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.cartUpdates?.timestamp]); // Only depend on timestamp to avoid re-running on every data object change

  return {
    cartUpdateData: data?.cartUpdates,
    subscriptionLoading: loading,
    subscriptionError: error,
  };
};